import { useState, useRef, useCallback, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import "./index.css";

const TRACK_LENGTH = 1000; // meters
const DT = 1 / 60; // timestep seconds
const MAX_SPEED = 300; // m/s cap for display sanity

interface SimState {
	position: number; // meters
	velocity: number; // m/s
	acceleration: number; // m/s^2
	time: number; // seconds
	running: boolean;
}

function formatNum(n: number, decimals = 1): string {
	return n.toFixed(decimals);
}

export function App() {
	const [mass, setMass] = useState(500); // kg
	const [thrust, setThrust] = useState(5000); // N
	const [sim, setSim] = useState<SimState>({
		position: 0,
		velocity: 0,
		acceleration: 0,
		time: 0,
		running: false,
	});
	const [trail, setTrail] = useState<number[]>([]);

	const animRef = useRef<number | null>(null);
	const simRef = useRef(sim);
	const massRef = useRef(mass);
	const thrustRef = useRef(thrust);

	useEffect(() => {
		simRef.current = sim;
	}, [sim]);
	useEffect(() => {
		massRef.current = mass;
	}, [mass]);
	useEffect(() => {
		thrustRef.current = thrust;
	}, [thrust]);

	const step = useCallback(() => {
		const s = simRef.current;
		if (!s.running) return;

		const a = thrustRef.current / massRef.current; // F = ma -> a = F/m
		let newVelocity = s.velocity + a * DT;
		if (newVelocity > MAX_SPEED) newVelocity = MAX_SPEED;
		const newPosition = s.position + newVelocity * DT;
		const newTime = s.time + DT;

		if (newPosition >= TRACK_LENGTH) {
			setSim({
				position: TRACK_LENGTH,
				velocity: newVelocity,
				acceleration: a,
				time: newTime,
				running: false,
			});
			setTrail((prev) => [...prev, TRACK_LENGTH]);
			return;
		}

		setSim({
			position: newPosition,
			velocity: newVelocity,
			acceleration: a,
			time: newTime,
			running: true,
		});
		setTrail((prev) => {
			const next = [...prev, newPosition];
			// Keep last 20 trail points
			return next.length > 20 ? next.slice(next.length - 20) : next;
		});

		animRef.current = requestAnimationFrame(step);
	}, []);

	const startSim = useCallback(() => {
		setSim({
			position: 0,
			velocity: 0,
			acceleration: thrust / mass,
			time: 0,
			running: true,
		});
		setTrail([]);
		// Start animation next frame
		setTimeout(() => {
			animRef.current = requestAnimationFrame(step);
		}, 0);
	}, [thrust, mass, step]);

	const resetSim = useCallback(() => {
		if (animRef.current) {
			cancelAnimationFrame(animRef.current);
			animRef.current = null;
		}
		setSim({
			position: 0,
			velocity: 0,
			acceleration: 0,
			time: 0,
			running: false,
		});
		setTrail([]);
	}, []);

	const togglePause = useCallback(() => {
		if (sim.running) {
			if (animRef.current) {
				cancelAnimationFrame(animRef.current);
				animRef.current = null;
			}
			setSim((prev) => ({ ...prev, running: false }));
		} else if (sim.position > 0 && sim.position < TRACK_LENGTH) {
			setSim((prev) => ({ ...prev, running: true }));
			setTimeout(() => {
				animRef.current = requestAnimationFrame(step);
			}, 0);
		}
	}, [sim.running, sim.position, step]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (animRef.current) cancelAnimationFrame(animRef.current);
		};
	}, []);

	const acceleration = mass > 0 ? thrust / mass : 0;
	const sledPercent = (sim.position / TRACK_LENGTH) * 100;
	const isFinished = sim.position >= TRACK_LENGTH;
	const isIdle = !sim.running && sim.position === 0;

	return (
		<div className="w-full h-full flex flex-col max-w-6xl mx-auto p-3 sm:p-4 gap-3 overflow-hidden">
			{/* Header */}
			<div className="text-center shrink-0">
				<h1 className="text-xl sm:text-2xl font-bold tracking-tight">
					Rocket Sled Simulator
				</h1>
				<p className="text-xs text-muted-foreground">
					Newton's Second Law: <span className="font-mono font-semibold text-foreground">F = ma</span>
					{" "}&rarr;{" "}
					<span className="font-mono font-semibold text-foreground">a = F / m</span>
				</p>
			</div>

			{/* Track Visualization */}
			<Card className="overflow-hidden border-2 flex-1 min-h-0">
				<CardContent className="p-0 h-full">
					{/* Sky */}
					<div className="relative bg-gradient-to-b from-sky-400 to-sky-200 h-full overflow-hidden">
						{/* Clouds */}
						<div className="absolute top-6 left-[10%] w-16 h-6 bg-white/70 rounded-full blur-sm" />
						<div className="absolute top-10 left-[30%] w-24 h-8 bg-white/60 rounded-full blur-sm" />
						<div className="absolute top-4 right-[20%] w-20 h-7 bg-white/50 rounded-full blur-sm" />

						{/* Mountains */}
						<div className="absolute bottom-8 left-0 right-0">
							<svg viewBox="0 0 800 80" className="w-full h-16 text-slate-300/60">
								<polygon points="0,80 100,20 200,60 300,10 450,50 550,5 650,40 750,15 800,50 800,80" fill="currentColor" />
							</svg>
						</div>

						{/* Ground / Track area */}
						<div className="absolute bottom-0 left-0 right-0 h-8 bg-amber-700/80" />

						{/* Track rail */}
						<div className="absolute bottom-6 left-4 right-4">
							<div className="h-2 bg-zinc-400 rounded-full shadow-inner relative">
								{/* Track ties */}
								{Array.from({ length: 21 }).map((_, i) => (
									<div
										key={i}
										className="absolute top-[-3px] w-1 h-[14px] bg-amber-900/60 rounded-sm"
										style={{ left: `${(i / 20) * 100}%` }}
									/>
								))}
							</div>

							{/* Distance markers */}
							<div className="flex justify-between mt-1 px-0.5">
								{[0, 250, 500, 750, 1000].map((m) => (
									<span key={m} className="text-[9px] font-mono text-white/80 drop-shadow">
										{m}m
									</span>
								))}
							</div>
						</div>

						{/* Exhaust trail */}
						{sim.running &&
							trail.slice(-12).map((pos, i) => {
								const p = (pos / TRACK_LENGTH) * 100;
								const trackPad = 1.5; // % padding for left/right
								const effectiveP = trackPad + (p * (100 - 2 * trackPad)) / 100;
								const opacity = ((i + 1) / 12) * 0.4;
								const size = 4 + (12 - i) * 1.5;
								return (
									<div
										key={`trail-${i}`}
										className="absolute rounded-full"
										style={{
											left: `calc(${effectiveP}% - ${size / 2}px)`,
											bottom: `${22 + Math.random() * 8}px`,
											width: `${size}px`,
											height: `${size}px`,
											background: `radial-gradient(circle, rgba(255,100,0,${opacity}) 0%, rgba(200,200,200,${opacity * 0.5}) 100%)`,
										}}
									/>
								);
							})}

						{/* Sled */}
						<div
							className="absolute transition-none"
							style={{
								left: `calc(${1.5 + (sledPercent * 97) / 100}% - 20px)`,
								bottom: "16px",
							}}
						>
							{/* Flame */}
							{sim.running && (
								<div className="absolute -left-4 top-[10px]">
									<div className="w-5 h-3 bg-gradient-to-l from-orange-500 via-yellow-400 to-transparent rounded-l-full animate-pulse" />
									<div className="w-3 h-2 bg-gradient-to-l from-red-500 via-orange-400 to-transparent rounded-l-full -mt-0.5 ml-0.5 animate-pulse" />
								</div>
							)}
							{/* Body */}
							<svg width="40" height="24" viewBox="0 0 40 24">
								{/* Sled body */}
								<polygon
									points="0,18 6,4 34,4 40,12 40,18"
									className="fill-red-600 stroke-red-800"
									strokeWidth="1"
								/>
								{/* Cockpit window */}
								<polygon points="28,6 34,6 36,11 30,11" className="fill-sky-300 stroke-sky-500" strokeWidth="0.5" />
								{/* Nozzle */}
								<rect x="0" y="10" width="4" height="8" rx="1" className="fill-zinc-600" />
								{/* Stripe */}
								<rect x="10" y="8" width="16" height="2" rx="1" className="fill-white/40" />
								{/* Skids */}
								<rect x="8" y="20" width="8" height="3" rx="1" className="fill-zinc-500" />
								<rect x="26" y="20" width="8" height="3" rx="1" className="fill-zinc-500" />
							</svg>
						</div>

						{/* Finish flag */}
						<div className="absolute right-3 bottom-10">
							<div className="w-0.5 h-10 bg-zinc-800" />
							<div className="absolute top-0 left-0.5 w-4 h-3 grid grid-cols-2 grid-rows-2">
								<div className="bg-white" />
								<div className="bg-black" />
								<div className="bg-black" />
								<div className="bg-white" />
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Readouts */}
			<div className="grid grid-cols-4 gap-2 shrink-0">
				<ReadoutCard label="Acceleration" value={formatNum(sim.running ? sim.acceleration : acceleration)} unit="m/s²" color="text-orange-500" />
				<ReadoutCard label="Velocity" value={formatNum(sim.velocity)} unit="m/s" color="text-blue-500" />
				<ReadoutCard label="Position" value={formatNum(sim.position, 0)} unit="m" color="text-green-500" />
				<ReadoutCard label="Time" value={formatNum(sim.time)} unit="s" color="text-purple-500" />
			</div>

			{/* Controls */}
			<Card className="shrink-0">
				<CardContent className="p-3 sm:p-4 space-y-3">
					{/* Equation display */}
					<div className="text-center p-2 bg-muted/50 rounded-lg font-mono text-sm">
						<span className="text-orange-500 font-bold">{formatNum(acceleration)}</span>
						<span className="text-muted-foreground"> m/s² </span>
						<span className="text-muted-foreground">= </span>
						<span className="text-red-500 font-bold">{thrust.toLocaleString()}</span>
						<span className="text-muted-foreground"> N </span>
						<span className="text-muted-foreground">/ </span>
						<span className="text-blue-500 font-bold">{mass}</span>
						<span className="text-muted-foreground"> kg</span>
					</div>

					{/* Sliders */}
					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-1.5">
							<div className="flex items-center justify-between">
								<Label className="text-xs font-medium flex items-center gap-1.5">
									<span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" />
									Thrust (F)
								</Label>
								<span className="font-mono text-xs font-semibold text-red-500">
									{thrust.toLocaleString()} N
								</span>
							</div>
							<Slider
								value={[thrust]}
								onValueChange={(v) => setThrust(v[0])}
								min={500}
								max={50000}
								step={500}
								disabled={sim.running}
								className="[&_[role=slider]]:bg-red-500 [&_[role=slider]]:border-red-600 [&_span:first-child>span]:bg-red-500/80"
							/>
							<div className="flex justify-between text-[9px] text-muted-foreground font-mono">
								<span>500 N</span>
								<span>50,000 N</span>
							</div>
						</div>

						<div className="space-y-1.5">
							<div className="flex items-center justify-between">
								<Label className="text-xs font-medium flex items-center gap-1.5">
									<span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block" />
									Mass (m)
								</Label>
								<span className="font-mono text-xs font-semibold text-blue-500">
									{mass} kg
								</span>
							</div>
							<Slider
								value={[mass]}
								onValueChange={(v) => setMass(v[0])}
								min={50}
								max={5000}
								step={50}
								disabled={sim.running}
								className="[&_[role=slider]]:bg-blue-500 [&_[role=slider]]:border-blue-600 [&_span:first-child>span]:bg-blue-500/80"
							/>
							<div className="flex justify-between text-[9px] text-muted-foreground font-mono">
								<span>50 kg</span>
								<span>5,000 kg</span>
							</div>
						</div>
					</div>

					{/* Buttons */}
					<div className="flex gap-3 justify-center">
						{isIdle ? (
							<Button onClick={startSim} size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8">
								Launch
							</Button>
						) : (
							<>
								<Button onClick={togglePause} size="lg" variant="outline" className="px-6" disabled={isFinished}>
									{sim.running ? "Pause" : "Resume"}
								</Button>
								<Button onClick={resetSim} size="lg" variant="secondary" className="px-6">
									Reset
								</Button>
							</>
						)}
					</div>

					{isFinished && (
						<p className="text-center text-xs font-medium text-green-600">
							Sled reached {TRACK_LENGTH}m in {formatNum(sim.time)}s at {formatNum(sim.velocity)} m/s
						</p>
					)}
				</CardContent>
			</Card>
		</div>
	);
}

function ReadoutCard({
	label,
	value,
	unit,
	color,
}: {
	label: string;
	value: string;
	unit: string;
	color: string;
}) {
	return (
		<Card>
			<CardContent className="p-2 text-center">
				<p className="text-[9px] uppercase tracking-wider text-muted-foreground">
					{label}
				</p>
				<p className={`text-lg sm:text-xl font-mono font-bold ${color}`}>
					{value}
				</p>
				<p className="text-[9px] text-muted-foreground">{unit}</p>
			</CardContent>
		</Card>
	);
}

export default App;
